"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { TypographyP, TypographyLabel, TypographySmallLabel, TypographyH2 } from '@/components/typography/typography'

export interface PricingFeature {
  text: string
  included: boolean
}

export interface PricingFeatureGroup {
  /** Category label shown above this group of features, e.g. "Core", "Support". */
  category: string
  features: PricingFeature[]
}

export interface PricingPlan {
  tier: string
  name: string
  description: string
  monthlyPrice: number | null
  yearlyPrice: number | null
  monthlyPriceId: string | null
  yearlyPriceId: string | null
  /**
   * Feature list for this plan. Pass a flat array of PricingFeature for a
   * single list, or an array of PricingFeatureGroup to group features under
   * category headers with separators between groups.
   */
  features: PricingFeature[] | PricingFeatureGroup[]
  highlighted: boolean
  oneTime: boolean
  perSeat: boolean
  currency: string
  cta: string
}

export interface PricingCardsProps {
  plans: PricingPlan[]
  userTier: string | null
  isSignedIn: boolean
}

function isFeatureGroups(
  features: PricingFeature[] | PricingFeatureGroup[]
): features is PricingFeatureGroup[] {
  return features.length > 0 && 'category' in features[0]
}

function FeatureRow({ feature }: { feature: PricingFeature }) {
  return (
    <li className="flex items-start gap-3 text-sm">
      {feature.included ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" strokeWidth={2} />
      ) : (
        <Minus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" strokeWidth={2} />
      )}
      <span className={cn('text-foreground', !feature.included && 'text-muted-foreground/50')}>
        {feature.text}
      </span>
    </li>
  )
}

function FeatureList({ features }: { features: PricingFeature[] | PricingFeatureGroup[] }) {
  if (isFeatureGroups(features)) {
    return (
      <div className="flex flex-col gap-6">
        {features.map((group, index) => (
          <div key={group.category} className="flex flex-col gap-3">
            {index > 0 && <Separator className="mb-3" />}
            <TypographySmallLabel className="text-muted-foreground">{group.category}</TypographySmallLabel>
            <ul className="flex flex-col gap-2.5">
              {group.features.map((feature) => (
                <FeatureRow key={feature.text} feature={feature} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {features.map((feature) => (
        <FeatureRow key={feature.text} feature={feature} />
      ))}
    </ul>
  )
}

/**
 * Editorial pricing grid: a monthly/yearly toggle, square bg-secondary
 * plan cards, and feature lists that can be flat or grouped by category
 * (with TypographySmallLabel headers and thin rules between groups).
 *
 * The highlighted plan shows a "Most popular" TypographyEyebrow label in
 * the top-right corner of its card.
 *
 * Self-contained: handles its own billing-interval toggle, checkout/portal
 * routing, and loading state. Expects the following endpoints to exist in
 * the consuming app:
 * - POST /api/stripe/create-checkout  body: { tier, interval } -> { url }
 * - POST /api/stripe/create-portal    -> { url }
 * and routes unauthenticated users to /sign-up (optionally with
 * ?plan=<tier>&interval=<interval> query params).
 */
export function PricingCards({ plans, userTier, isSignedIn }: PricingCardsProps) {
  const [interval, setInterval] = React.useState<'monthly' | 'yearly'>('monthly')
  const [loadingTier, setLoadingTier] = React.useState<string | null>(null)
  const router = useRouter()

  type PlanAction = 
  | { type: 'noop' }
  | { type: 'sign-up' }
  | { type: 'sign-up-with-plan'; tier: string; interval: 'monthly' | 'yearly' }
  | { type: 'manage-subscription' }
  | { type: 'checkout'; tier: string; interval: 'monthly' | 'yearly' }

  function determinePlanAction(
    plan: PricingPlan,
    userTier: string | null,
    isSignedIn: boolean,
    interval: 'monthly' | 'yearly',
  ): PlanAction {
    const isFreePlan = plan.tier === 'free'
    const hasPaidSubscription = userTier !== null && userTier !== 'free'

    if (isSignedIn) {
      if (isFreePlan) {
        // Already using the product on some tier — nothing to do.
        return { type: 'noop' }
      } else {
        if (hasPaidSubscription) {
          // Any existing paid tier → always the portal, regardless of which
          // plan card was clicked. Changing plans is a portal action, not a
          // new checkout — true whether it's an upgrade, downgrade, or a
          // click on the same tier they're already on.
          return { type: 'manage-subscription' }
        } else {
          // Signed in, no paid subscription yet → genuinely new checkout.
          return { type: 'checkout', tier: plan.tier, interval }
        }
      }
    } else {
      if (isFreePlan) {
        return { type: 'sign-up' }
      } else {
        return { type: 'sign-up-with-plan', tier: plan.tier, interval }
      }
    }
  }

  async function handleSelectPlan(plan: PricingPlan) {
    const action = determinePlanAction(plan, userTier, isSignedIn, interval)

    switch (action.type) {
      case 'noop':
        return

      case 'sign-up':
        router.push('/sign-up')
        return

      case 'sign-up-with-plan':
        router.push(`/sign-up?plan=${action.tier}&interval=${action.interval}`)
        return

      case 'manage-subscription': {
        const res = await fetch('/api/stripe/create-portal', { method: 'POST' })
        const { url } = await res.json()
        if (url) window.location.href = url
        return
      }

      case 'checkout': {
        setLoadingTier(action.tier)
        try {
          const res = await fetch('/api/stripe/create-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tier: action.tier, interval: action.interval }),
          })
          const data = await res.json()
          if (data.url) window.location.href = data.url
        } finally {
          setLoadingTier(null)
        }
        return
      }
    }
  }

  const yearlyDiscount = (plan: PricingPlan): number | null => {
    if (!plan.monthlyPrice || !plan.yearlyPrice) return null
    return Math.round((1 - plan.yearlyPrice / plan.monthlyPrice) * 100)
  }

  const showToggle = plans.some((plan) => plan.yearlyPrice !== null)

  return (
    <div className="flex flex-col items-center gap-6">
      {showToggle && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => setInterval('monthly')}
            variant={interval === 'monthly' ? 'default' : 'outline'}
            size="sm"
          >
            Monthly
          </Button>
          <Button
            type="button"
            onClick={() => setInterval('yearly')}
            variant={interval === 'yearly' ? 'default' : 'outline'}
            size="sm"
          >
            Yearly
          </Button>
        </div>
      )}

      <div
        className={cn(
          'w-full grid gap-6',
          plans.length === 2 && 'md:grid-cols-2',
          plans.length === 3 && 'md:grid-cols-3',
          plans.length >= 4 && 'md:grid-cols-2 lg:grid-cols-4'
        )}
      >
        {plans.map((plan) => {
          const isCurrent = userTier === plan.tier
          const isLower = userTier && plan.tier === "free" || false
          const isLoading = loadingTier === plan.tier
          const price =
            plan.monthlyPrice === null
              ? null
              : interval === 'yearly' && plan.yearlyPrice !== null
              ? plan.yearlyPrice
              : plan.monthlyPrice
          const discount = yearlyDiscount(plan)

          return (
            <div key={plan.tier} className="relative flex flex-col bg-secondary p-6">
              {plan.highlighted && (
                <TypographyLabel className="absolute right-6 top-6">
                  Most popular
                </TypographyLabel>
              )}

              <div className="mb-12">
                <TypographyH2 noMargin>{plan.name}</TypographyH2>
                <TypographyP noMargin className="mt-1 text-muted-foreground">
                  {plan.description}
                </TypographyP>
              </div>

              <div className="mb-12">
                {price === null ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl md:text-6xl font-bold tracking-tight">
                      {plan.currency}0
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-4xl md:text-6xl font-bold tracking-tight">
                      {plan.currency}
                      {price}
                    </span>
                    {plan.perSeat && (
                      <TypographySmallLabel className="text-muted-foreground">/ seat</TypographySmallLabel>
                    )}
                    {!plan.oneTime && (
                      <TypographySmallLabel className="text-muted-foreground">
                        / month
                      </TypographySmallLabel>
                    )}
                    {interval === 'yearly' && discount && (
                      <Badge variant="default">
                        -{discount}%
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="button"
                className="mb-12 w-full"
                variant="default"
                disabled={isCurrent || isLoading || isLower }
                onClick={() => handleSelectPlan(plan)}
              >
                {isLoading ? 'Loading…' : isCurrent ? 'Current plan' : plan.cta}
              </Button>

              <FeatureList features={plan.features} />
            </div>
          )
        })}
      </div>
      <div className="w-full flex flex-row justify-start items-center text-sm font-normal text-muted-foreground/50">
        Currently available in the US and the EU.
      </div>
    </div>
  )
}